import { Fragment, useMemo, useState } from 'react';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import BYDBanner from './Asset/banner-danza-d9.webp';
import BYDBannerMobile from './Asset/danza-d9-banner-mob.webp';
import DenzaD9Interior from './Asset/interior.webp';
import DenzaD9Sunroof from './Asset/m07-pc-skylight.webp';
import DenzaD9brown from './Asset/interior-brown.webp';
import D9Banner2026 from './images/danza-d9-2026/d9-banner-2026.jpg';
import D9PricePoster from './images/danza-d9-2026/d9-nik-2026-harga.jpg';
import D9ColorBlue from './images/danza-d9-2026/color-blue.png';
import D9ColorBlack from './images/danza-d9-2026/color-black.png';
import D9ColorWhite from './images/danza-d9-2026/color-white.png';
import D9ColorGrey from './images/danza-d9-2026/color-grey.png';
import CarColorShowcase from './CarColorShowcase';
import PageSeo, {
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from './PageSeo';
import ModelPriceSummary from './ModelPriceSummary';
import { createWhatsAppUrl } from './siteConfig';
import { vehiclePrices } from './vehiclePrices';

const D9_PRICE = vehiclePrices.denzaD9[0];

// Nama warna, gradasi swatch, dan render mobil mengikuti data resmi danza.co.id.
const exteriorColors = [
  { label: 'Whale Sea Blue', start: '#97A9C5', end: '#284063', image: D9ColorBlue },
  { label: 'Cosmos Black', start: '#9AA1AA', end: '#030303', image: D9ColorBlack },
  { label: 'Arctic White', start: '#FBFAF9', end: '#CDD1D4', image: D9ColorWhite },
  { label: 'Starry Night Grey', start: '#A9ACAF', end: '#4F5357', image: D9ColorGrey },
];

const showcaseStats = [
  { label: 'Jarak tempuh NEDC', value: 730, unit: 'km' },
  { label: 'Tenaga maksimum', value: 340, unit: 'kW' },
  { label: '0-100 km/jam', value: 8.5, unit: 'detik', decimals: 1 },
];

// Perbandingan D9 generasi pertama vs New D9 2nd Generation (FWD),
// mengikuti materi product knowledge resmi Danza Indonesia.
const comparisonGroups = [
  {
    group: 'Dimensi',
    rows: [
      ['Dimensi (P x L x T)', '5.250 x 1.960 x 1.920 mm', '5.250 x 1.960 x 1.900 mm'],
      ['Radius putar minimum', '5,95 m', '5,85 m'],
      ['Kapasitas bagasi (min / maks)', '410 / 2.310 L', '689 / 2.436 L'],
    ],
  },
  {
    group: 'Performa',
    rows: [
      ['Penggerak', 'FWD', 'FWD'],
      ['Tenaga motor depan', '230 kW', '340 kW'],
      ['Torsi motor depan', '360 Nm', '365 Nm'],
      ['Akselerasi 0-100 km/jam', '9,5 detik', '8,5 detik'],
      ['Suspensi', 'FSD (Frequency Selective Damping)', 'DiSus-C Intelligent Damping Body Control'],
      ['Ban', 'Michelin 235/60 R18', 'Bridgestone HL 235/60 R18'],
    ],
  },
  {
    group: 'Energi',
    rows: [
      ['Baterai', 'Blade Battery 103,36 kWh', 'Blade Battery 2.0 115,07 kWh'],
      ['Jarak tempuh NEDC', '600 km', '730 km'],
      ['Pengisian cepat', 'DC fast charging', 'Flash Charging, 0-97% sekitar 9 menit'],
    ],
  },
  {
    group: 'ADAS & Keselamatan',
    rows: [
      ['Airbag', '8', '9 (tambahan far side airbag penumpang depan)'],
      ['Front Cross Traffic Alert / Braking (FCTA / FCTB)', 'Tidak ada', 'Ada'],
      ['Child Presence Detection (CPD)', 'Tidak ada', 'Ada'],
      ['Driver Fatigue Monitoring (DFM)', 'Tidak ada', 'Ada'],
      ['Dashcam / Driving Video Recorder', 'Tidak ada', 'Ada'],
      ['Memori posisi spion luar', 'Tidak ada', 'Ada'],
      ['Seat belt reminder', 'Baris 1 dan 2', 'Baris 1, 2, dan 3'],
      ['Spion tengah', 'Manual', 'Auto-dimming'],
    ],
  },
  {
    group: 'Kenyamanan Kabin',
    rows: [
      ['Kursi baris 1', 'Ventilasi + memori', 'Ventilasi + memori + pijat + pemanas'],
      ['Pijat kursi baris 2', '6 titik', '10 titik'],
      ['Kursi baris 3', 'Manual 4 arah', 'Elektrik 4 arah + lipat elektrik'],
      ['Pengaturan tata letak kursi otomatis', 'Tidak ada', 'Ada (satu sentuhan di layar)'],
      ['Audio', 'Dynaudio 14 speaker', 'Devialet 16 speaker'],
      ['Wireless charging depan 50 W', '1 unit', '2 unit'],
      ['Voice command', '4 zona', '6 zona'],
      ['Transmisi', 'Gear selector di konsol tengah', 'Steering column shifter, konsol lebih lega'],
    ],
  },
];

const extraFeatures = [
  {
    title: 'Penyesuaian tata letak otomatis',
    desc: 'Ubah konfigurasi kursi lewat layar: 4 penumpang, 7 penumpang, atau 7 penumpang dengan bagasi penuh.',
  },
  {
    title: 'Tombol kursi baris 3 di bagasi',
    desc: 'Lipat atau atur kursi baris ketiga langsung dari bagasi tanpa masuk ke kabin.',
  },
  {
    title: 'Child Presence Detection',
    desc: 'Radar mendeteksi napas dan detak jantung dalam 15 detik setelah mobil dikunci, lalu membunyikan peringatan dan menyalakan AC bila kabin terlalu panas.',
  },
  {
    title: 'Driver Monitor System',
    desc: 'Kamera memantau kedipan, menguap, dan posisi kepala untuk mengingatkan pengemudi yang mulai lelah.',
  },
  {
    title: 'Dashcam bawaan (DVR)',
    desc: 'Merekam perjalanan secara otomatis sebagai bukti bila terjadi insiden di jalan.',
  },
  {
    title: 'Kontrol pintu dan jendela baris 2',
    desc: 'Penumpang baris kedua mengatur jendela dan pintu elektrik sendiri.',
  },
  {
    title: 'Smart Scene Mode',
    desc: 'Preset suasana untuk situasi tertentu: mode navigasi, pembersihan layar, dan cuci mobil.',
  },
  {
    title: 'Penyimpanan bawah bagasi',
    desc: 'Kompartemen tambahan di lantai bagasi untuk barang kecil tanpa mengganggu ruang utama.',
  },
  {
    title: 'ADAS lengkap',
    desc: 'FCTA/FCTB, RCTA/RCTB, Lane Keeping Assist, Blind Spot Monitoring, Lane Departure Warning, dan Lane Change Assistance.',
  },
];

function JelajahiDenza() {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [selectedInterior, setSelectedInterior] = useState('brown');

  const interiorOptions = useMemo(
    () => [
      {
        label: 'Brown',
        value: 'brown',
        swatch: 'linear-gradient(135deg, #7a4e35 0%, #2b1f1a 100%)',
        image: DenzaD9brown,
      },
      {
        label: 'Black (Baru NIK 2026)',
        value: 'black',
        swatch: 'linear-gradient(135deg, #3a3d42 0%, #0b0c0e 100%)',
        image: null,
      },
    ],
    [],
  );

  const currentInterior =
    interiorOptions.find((option) => option.value === selectedInterior) ||
    interiorOptions[0];

  return (
    <>
      <PageSeo
        title='Danza D9 NIK 2026 BSD Serpong | Harga Rp1,08 Miliar OTR Jakarta'
        description='Jelajahi Danza D9 NIK 2026 di BYD BSD Serpong. Harga Rp1.080.000.000 OTR Jakarta, Blade Battery Gen 2 115 kWh, 340 kW, 730 km NEDC, Super Flash Charging, interior baru Black, promo, dan test drive.'
        keywords={[
          ...keywordSets.danzaD9,
          ...keywordSets.danza,
          ...keywordSets.localDealer,
          'MPV listrik premium',
          'Danza D9 interior black',
          'Danza D9 Devialet',
        ]}
        path='/jelajahi-denzad9'
        image={BYDBanner}
        jsonLd={[
          productJsonLd({
            name: 'Danza D9',
            alternateName: 'Denza D9',
            description:
              'MPV listrik premium Danza D9 NIK 2026 dengan Blade Battery Generasi 2, 340 kW, jarak tempuh 730 km NEDC, captain seat, dan interior baru Black.',
            image: BYDBanner,
            path: '/jelajahi-denzad9',
            price: D9_PRICE.priceValue,
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Danza D9', path: '/jelajahi-denzad9' },
          ]),
        ]}
      />

      <Box component='main' sx={{ width: '100%', overflow: 'hidden' }}>
        <Box
          component='h1'
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          Jelajahi Danza D9
        </Box>

        <HeroSection isMobile={isMobile} />

        <CarColorShowcase
          title='Danza D9'
          kicker='Exterior'
          description='Memadukan nada warna yang hidup dan natural, Danza D9 mewujudkan keindahan yang tak lekang waktu. Empat warna resmi untuk NIK 2026.'
          colors={exteriorColors}
          defaultColor='Cosmos Black'
          imageRatio={33.3}
          stats={showcaseStats}
          cta={{
            label: 'Tanya Harga',
            href: createWhatsAppUrl(
              `Halo Junius, saya tertarik dengan Danza D9 NIK 2026 (${D9_PRICE.price}). Mohon info warna, promo, dan ketersediaan unit.`,
            ),
            external: true,
          }}
        />

        <InteriorColorSection
          currentInterior={currentInterior}
          interiorOptions={interiorOptions}
          selectedInterior={selectedInterior}
          setSelectedInterior={setSelectedInterior}
        />

        <WhatsNewSection />

        <FeatureImageSection
          image={DenzaD9Sunroof}
          title='Panoramic Sunroof'
          description='Sunroof panoramik ekstra besar membawa cahaya alami ke dalam kabin, membuat ruang terasa lapang, terang, dan tenang untuk perjalanan jauh.'
          imageFit='cover'
        />

        <FeatureImageSection
          image={DenzaD9Interior}
          title='Executive Cabin'
          description='Kabin Danza D9 dirancang untuk kenyamanan keluarga dan perjalanan bisnis, dengan ruang luas, material premium, dan suasana interior yang tenang.'
          imageFit='cover'
        />

        <ModelPriceSummary model='Danza D9' priceKey='denzaD9' />

        <CtaSection />
      </Box>
    </>
  );
}

function HeroSection({ isMobile }) {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '92vh', md: '100vh' },
        pt: { xs: 9, md: 10 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        backgroundColor: '#0f1115',
      }}
    >
      <Box
        component='img'
        src={isMobile ? BYDBannerMobile : D9Banner2026}
        alt='Danza D9 NIK 2026 premium electric MPV'
        sx={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0, left: 0,
          width: '100%',
          height: '100%',
          objectFit: { xs: 'cover', md: 'cover' },
          objectPosition: 'center',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0, left: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.48) 0%, rgba(0,0,0,0.12) 44%, rgba(0,0,0,0.58) 100%)',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 1200,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          alignSelf: 'flex-end',
          pb: { xs: 5, md: 7 },
          color: '#fff',
        }}
      >
        <Typography
          sx={{
            display: 'inline-block',
            mb: 1.5,
            px: 1.4,
            py: 0.5,
            borderRadius: '999px',
            backgroundColor: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(255,255,255,0.28)',
            fontSize: '0.8rem',
            fontWeight: 800,
            letterSpacing: 0.6,
          }}
        >
          NIK 2026 · {D9_PRICE.price} OTR Jakarta · Interior baru Black
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '2.25rem', sm: '3.2rem', md: '4.5rem' },
            fontWeight: 900,
            lineHeight: 1,
            maxWidth: 760,
          }}
        >
          Danza D9
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 650,
            fontSize: { xs: '1rem', md: '1.2rem' },
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.9)',
          }}
        >
          Generasi kedua MPV listrik premium: Blade Battery 2.0, jarak tempuh
          730 km NEDC, suspensi DiSus-C, dan audio Devialet 16 speaker untuk
          kenyamanan elegan di setiap perjalanan.
        </Typography>

        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
            gap: 1,
            maxWidth: 900,
          }}
        >
          {[
            { value: '730 km', label: 'NEDC range' },
            { value: '340 kW', label: 'Tenaga maksimum' },
            { value: '9 menit', label: 'Flash Charging 0-97%' },
            { value: '7 kursi', label: 'Premium cabin' },
          ].map((spec) => (
            <Box
              key={spec.label}
              sx={{
                border: '1px solid rgba(255,255,255,0.26)',
                backgroundColor: 'rgba(0,0,0,0.36)',
                px: 2,
                py: 1.5,
                borderRadius: '8px',
              }}
            >
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800 }}>
                {spec.value}
              </Typography>
              <Typography sx={{ fontSize: '0.88rem', opacity: 0.82 }}>
                {spec.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function InteriorColorSection({
  currentInterior,
  interiorOptions,
  selectedInterior,
  setSelectedInterior,
}) {
  return (
    <Box
      sx={{
        minHeight: { xs: 'auto', md: '100vh' },
        backgroundColor: '#111827',
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '360px 1fr' },
          gap: { xs: 4, lg: 5 },
          alignItems: 'center',
        }}
      >
        <SelectorPanel
          eyebrow='Selected interior'
          title={currentInterior.label}
          description='Pilih nuansa kabin untuk pengalaman berkendara yang lebih personal.'
          options={interiorOptions}
          selectedValue={selectedInterior}
          onSelect={setSelectedInterior}
          dark
        />

        <Box>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.72)',
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontSize: '0.82rem',
            }}
          >
            Interior Color
          </Typography>
          <Typography
            component='h2'
            sx={{
              mt: 1,
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              color: '#fff',
              lineHeight: 1.05,
            }}
          >
            Kabin yang terasa tenang
          </Typography>
          <Box
            sx={{
              mt: 3,
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 24px 42px rgba(0,0,0,0.34)',
              backgroundColor: '#0b0f16',
            }}
          >
            {currentInterior.image ? (
              <Box
                component='img'
                src={currentInterior.image}
                alt={`Interior Danza D9 ${currentInterior.label}`}
                sx={{
                  width: '100%',
                  height: { xs: 280, sm: 420, md: 540 },
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: { xs: 280, sm: 420, md: 540 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 3,
                  background:
                    'radial-gradient(circle at 50% 30%, #2a2d33 0%, #0b0c0e 70%)',
                  color: '#fff',
                }}
              >
                <Typography sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' }, fontWeight: 900 }}>
                  Interior Black
                </Typography>
                <Typography sx={{ mt: 1, color: 'rgba(255,255,255,0.72)', maxWidth: 420 }}>
                  Pilihan baru untuk Danza D9 NIK 2026. Foto resmi menyusul,
                  hubungi Junius untuk melihat unit di showroom.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SelectorPanel({
  eyebrow,
  title,
  description,
  options,
  selectedValue,
  onSelect,
  dark = false,
}) {
  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: dark
          ? '1px solid rgba(255,255,255,0.18)'
          : '1px solid rgba(17,24,39,0.12)',
        backgroundColor: dark ? 'rgba(255,255,255,0.06)' : '#fff',
        color: dark ? '#fff' : '#111827',
        p: { xs: 2, sm: 3 },
        boxShadow: dark ? 'none' : '0 12px 26px rgba(16,24,40,0.08)',
      }}
    >
      <Typography
        sx={{
          color: dark ? 'rgba(255,255,255,0.66)' : '#667085',
          fontWeight: 800,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
          fontSize: '0.78rem',
        }}
      >
        {eyebrow}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: '2rem', fontWeight: 900 }}>
        {title}
      </Typography>
      <Typography
        sx={{
          mt: 1,
          lineHeight: 1.6,
          color: dark ? 'rgba(255,255,255,0.72)' : '#4b5563',
        }}
      >
        {description}
      </Typography>

      <Box sx={{ mt: 3, display: 'grid', gap: 1 }}>
        {options.map((option) => {
          const active = selectedValue === option.value;

          return (
            <Button
              key={option.value}
              type='button'
              onClick={() => onSelect(option.value)}
              variant='outlined'
              sx={{
                justifyContent: 'flex-start',
                gap: 1.5,
                py: 1.2,
                px: 1.3,
                borderRadius: '8px',
                textTransform: 'none',
                color: dark ? '#fff' : '#111827',
                borderColor: active
                  ? '#2d6cdf'
                  : dark
                    ? 'rgba(255,255,255,0.22)'
                    : 'rgba(17,24,39,0.16)',
                backgroundColor: active
                  ? dark
                    ? 'rgba(45,108,223,0.22)'
                    : '#edf4ff'
                  : 'transparent',
                '&:hover': {
                  borderColor: '#2d6cdf',
                  backgroundColor: active
                    ? dark
                      ? 'rgba(45,108,223,0.28)'
                      : '#e4efff'
                    : dark
                      ? 'rgba(255,255,255,0.08)'
                      : '#f7f9fc',
                },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  flex: '0 0 auto',
                  background: option.swatch,
                  border:
                    option.value === 'white'
                      ? '1px solid rgba(17,24,39,0.24)'
                      : '1px solid rgba(255,255,255,0.42)',
                  boxShadow: active ? '0 0 0 3px rgba(45,108,223,0.22)' : '',
                }}
              />
              <Typography sx={{ fontWeight: 800 }}>{option.label}</Typography>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

function FeatureImageSection({ image, title, description, imageFit }) {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '82vh', md: '100vh' },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        backgroundColor: '#0f1115',
      }}
    >
      <Box
        component='img'
        src={image}
        alt={title}
        sx={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0, left: 0,
          width: '100%',
          height: '100%',
          objectFit: imageFit,
          objectPosition: 'center',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0, left: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.62) 100%)',
        }}
      />
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 980,
          px: { xs: 2, md: 6 },
          pb: { xs: 5, md: 7 },
          color: '#fff',
        }}
      >
        <Typography
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3.5rem' },
            fontWeight: 900,
            lineHeight: 1.05,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 720,
            fontSize: { xs: '1rem', md: '1.12rem' },
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.88)',
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

function WhatsNewSection() {
  return (
    <Box
      component='section'
      id='d9-2nd-gen'
      aria-label='Yang baru di Danza D9 NIK 2026'
      sx={{ backgroundColor: '#f4f6f8', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '380px 1fr' },
          gap: { xs: 3, lg: 5 },
          alignItems: 'start',
        }}
      >
        <Box>
          <Typography
            sx={{
              color: '#4c5867',
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontSize: '0.82rem',
            }}
          >
            NIK 2026
          </Typography>
          <Typography
            component='h2'
            sx={{
              mt: 1,
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              color: '#111827',
              lineHeight: 1.05,
            }}
          >
            Yang baru di Danza D9
          </Typography>
          <Typography sx={{ mt: 1.5, color: '#4b5563', lineHeight: 1.7 }}>
            Harga {D9_PRICE.price} OTR Jakarta. Generasi kedua D9 tetap
            berpenggerak depan (FWD), kini dengan Blade Battery 2.0, tenaga
            lebih besar, jarak tempuh lebih jauh, suspensi DiSus-C, audio
            Devialet 16 speaker, dan fitur keselamatan tambahan. Emblem
            belakang baru membuat tampilannya lebih bersih.
          </Typography>
          <Box
            component='img'
            src={D9PricePoster}
            alt='Harga Danza D9 NIK 2026 IDR 1.080.000.000 OTR Jakarta'
            loading='lazy'
            sx={{
              mt: 3,
              width: '100%',
              height: 'auto',
              borderRadius: '16px',
              display: 'block',
              boxShadow: '0 20px 44px rgba(16,24,40,0.16)',
            }}
          />
        </Box>

        <Box
          component='table'
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: '#fff',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid rgba(17,24,39,0.08)',
            fontSize: { xs: '0.86rem', md: '0.95rem' },
            '& th, & td': {
              px: { xs: 1.2, md: 2 },
              py: 1.1,
              borderTop: '1px solid rgba(17,24,39,0.08)',
              verticalAlign: 'top',
              textAlign: 'left',
            },
            '& thead th': {
              backgroundColor: '#1f2937',
              color: '#fff',
              fontWeight: 900,
              borderTop: 0,
            },
            '& .group td': {
              backgroundColor: '#eef2f7',
              fontWeight: 900,
              color: '#1f2937',
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              fontSize: '0.78rem',
            },
            '& td:nth-of-type(1)': { color: '#4b5563', width: '34%' },
            '& td:nth-of-type(2)': { color: '#6b7280', width: '30%' },
            '& td:nth-of-type(3)': { color: '#111827', fontWeight: 800 },
          }}
        >
          <thead>
            <tr>
              <th>Spesifikasi</th>
              <th>D9 sebelumnya</th>
              <th>New D9 2nd Gen (FWD)</th>
            </tr>
          </thead>
          <tbody>
            {comparisonGroups.map((group) => (
              <Fragment key={group.group}>
                <tr className='group'>
                  <td colSpan={3}>{group.group}</td>
                </tr>
                {group.rows.map(([label, oldValue, newValue]) => (
                  <tr key={label}>
                    <td>{label}</td>
                    <td>{oldValue}</td>
                    <td>{newValue}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1240, mx: 'auto', mt: { xs: 5, md: 7 } }}>
        <Typography
          component='h3'
          sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 900, color: '#111827' }}
        >
          Fitur tambahan generasi kedua
        </Typography>
        <Box
          sx={{
            mt: 2.5,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {extraFeatures.map((item) => (
            <Box
              key={item.title}
              sx={{
                p: 2,
                borderRadius: '12px',
                backgroundColor: '#fff',
                border: '1px solid rgba(17,24,39,0.08)',
              }}
            >
              <Typography sx={{ fontWeight: 900, color: '#111827' }}>
                {item.title}
              </Typography>
              <Typography sx={{ mt: 0.5, color: '#4b5563', lineHeight: 1.55, fontSize: '0.95rem' }}>
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CtaSection() {
  return (
    <Box
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#f4f6f8',
      }}
    >
      <Box
        sx={{
          maxWidth: 980,
          mx: 'auto',
          textAlign: 'center',
        }}
      >
        <Typography
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
          }}
        >
          Tertarik dengan Danza D9?
        </Typography>
        <Typography
          sx={{
            mt: 2,
            mx: 'auto',
            maxWidth: 780,
            color: '#4b5563',
            lineHeight: 1.7,
          }}
        >
          Hubungi Junius di BYD BSD Serpong untuk info Danza D9 NIK 2026 (
          {D9_PRICE.price} OTR Jakarta), promo terbaru, simulasi pembelian, dan
          jadwal test drive.
        </Typography>
        <Button
          href={createWhatsAppUrl(
            `Halo Junius, saya tertarik dengan Danza D9 NIK 2026 (${D9_PRICE.price}). Mohon info harga, interior Black, dan promonya.`,
          )}
          target='_blank'
          rel='noopener noreferrer'
          variant='contained'
          sx={{
            mt: 3,
            px: 4,
            py: 1.25,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 800,
            backgroundColor: '#1f5fd1',
            '&:hover': { backgroundColor: '#184ca8' },
          }}
        >
          Tanya Harga Danza D9
        </Button>
      </Box>
    </Box>
  );
}

export default JelajahiDenza;
